import { useMemo } from 'react';
import { useCatalog, useCategories, useLandingPackages } from '../../../../storefront/lib/hooks';
import type { DisplayProduct } from '../../../../storefront/lib/types';
import { FLAVOURS, FLAVOUR_ORDER, type FlavourPreset } from '../tokens';

/** How many catalog rows stand in for an un-curated flavour shelf. */
const FLAVOUR_FALLBACK_COUNT = 2;

/**
 * The flavour spec cards in section 02 — FLX's own retail range.
 *
 * Deliberately does NOT filter to `isKit`: the launch flavours are ordinary
 * retail sachets, so a kit filter here would show an empty shelf on a tenant
 * whose whole catalog is two pouches. Kits sell from the catalog tail below.
 *
 * Curated order first ("Landing packages" in admin is exactly this control, and
 * the route re-sorts to the configured order so the landing can map a card to
 * its preset art by index); else the first couple of house products from the
 * live catalog. With nothing at all the caller keeps the static presets rather
 * than rendering an empty section.
 */
export function pickFlavourCards(
  landing: DisplayProduct[],
  catalog: DisplayProduct[],
): DisplayProduct[] {
  // House products only: this shelf is FLX's own line-up, not a marketplace.
  const house = (rows: DisplayProduct[]) => rows.filter((p) => p.sellerId === '');
  const curated = house(landing);
  if (curated.length > 0) return curated;
  return house(catalog).slice(0, FLAVOUR_FALLBACK_COUNT);
}

/**
 * Re-price curated rows from the auth-aware catalog.
 *
 * `useLandingPackages` reads the PUBLIC endpoint, which carries SRP and no
 * per-buyer context — so a signed-in member with a tier discount saw the sticker
 * price on the card while checkout charged them less. Anything the priced feed
 * knows about wins; a curated row it hasn't loaded yet (or that the member-priced
 * catalog legitimately omits) keeps its public row rather than vanishing.
 */
export function withMemberPricing(
  curated: DisplayProduct[],
  priced: DisplayProduct[],
): DisplayProduct[] {
  if (priced.length === 0) return curated;
  const byId = new Map(priced.map((p) => [p.id, p]));
  return curated.map((p) => byId.get(p.id) ?? p);
}

/** Preset for a catalog row: by SKU, else by curated position, else none. */
function resolvePreset(product: DisplayProduct, index: number): FlavourPreset | null {
  return FLAVOURS[product.sku.toUpperCase()] ?? FLAVOUR_ORDER[index] ?? null;
}

type ShelfEntry = { product: DisplayProduct | null; preset: FlavourPreset | null };

/** Pair catalog rows with presets, topping up from the presets when short. */
export function buildShelf(products: DisplayProduct[]): ShelfEntry[] {
  const entries: ShelfEntry[] = products.map((product, i) => ({
    product,
    preset: resolvePreset(product, i),
  }));
  if (entries.length >= FLAVOUR_ORDER.length) return entries;
  // Fewer real products than launch flavours — keep "Two ways to fuel" honest by
  // showing the remaining label(s) without a price or an Add button.
  const claimed = new Set(entries.map((e) => e.preset?.sku).filter(Boolean));
  return [
    ...entries,
    ...FLAVOUR_ORDER.filter((f) => !claimed.has(f.sku)).map((preset) => ({
      product: null,
      preset,
    })),
  ];
}

/**
 * Products for a category chip. `null` = "All", and a chip only ever narrows the
 * grid — filtering happens client-side because the whole FLX catalog is small
 * enough to arrive in one request (`useCatalog` already fetches it all).
 */
export function filterByCategory(
  products: DisplayProduct[],
  categoryId: string | null,
): DisplayProduct[] {
  if (!categoryId) return products;
  return products.filter((p) => p.categoryId === categoryId);
}

/**
 * Category chips, limited to the categories the visible catalog actually uses —
 * a chip that filters to nothing is worse than no chip.
 */
export function usedCategories(
  products: DisplayProduct[],
  categories: Array<{ id: string; name: string }>,
): Array<{ id: string; name: string }> {
  const present = new Set(products.map((p) => p.categoryId).filter(Boolean));
  return categories.filter((c) => present.has(c.id));
}

/**
 * Everything the shop section needs, in one hook.
 *
 * `useCatalog` is auth-aware for free: logged out it serves public SRP, and once
 * the buyer signs in it re-fetches at their member price (the drawer's prices
 * are re-synced by `useSyncCartPricesOnLogin`, mounted in the shell).
 */
export function useFlxShop(categoryId: string | null) {
  const { products, isLoading } = useCatalog();
  const landing = useLandingPackages();
  const categories = useCategories();

  const houseProducts = useMemo(() => products.filter((p) => p.sellerId === ''), [products]);
  // Curated rows come from the public endpoint; re-price them off the auth-aware
  // feed before anything renders a peso figure.
  const curated = useMemo(
    () => withMemberPricing(landing.products, products),
    [landing.products, products],
  );
  const flavourCards = useMemo(() => pickFlavourCards(curated, products), [curated, products]);
  // The tail is the rest of the house range: whatever section 02 is NOT already
  // showing as a flavour spec card. Without this the two launch pouches would
  // appear twice on the same screen.
  const tail = useMemo(() => {
    const shown = new Set(flavourCards.map((p) => p.id));
    return houseProducts.filter((p) => !shown.has(p.id));
  }, [houseProducts, flavourCards]);
  const chips = useMemo(() => usedCategories(tail, categories.data ?? []), [tail, categories.data]);
  const visible = useMemo(() => filterByCategory(tail, categoryId), [tail, categoryId]);

  return {
    flavourCards,
    products: visible,
    chips,
    isLoading: isLoading || landing.isLoading,
    /** No products at all — the section keeps its static marketing copy. */
    isEmpty: !isLoading && houseProducts.length === 0 && landing.products.length === 0,
  };
}
